import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, take } from 'rxjs';

import { CatsActionModeEnum, CatsRecordTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, findUnique } from '@shared-helpers/array-object-helpers';
import { getFilterString, getWeekStartingDate } from '@shared-helpers/date-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import { IOutgoingCatRecords, IOutgoingCatsHeadToTso } from '@shared-interfaces/cats-db';

import { ICatRecord } from '../interfaces/incoming';
import { ISapRequestFilterQueryProps } from '../services/api.model';
import { DatahubService } from '../services/datahub.service';
import { CatsRecord } from './catsRecord';
import { DateHandler } from './date-handler';
import { ExtDocument } from './extDocument';

export class CatsDb {
  constructor(private readonly datahub: DatahubService) {}

  getPayrollRecords$(
    userExtDocuments: ExtDocument[],
    reports: string[],
    dateHandler: DateHandler,
    selectString: string,
  ): Observable<IMixedCatsType> {
    const fromDate = dateHandler.getFilterString(dateHandler.getWeekStartingDate());
    const documentParams: ISapRequestFilterQueryProps = {
      Extdocumentno: userExtDocuments.map((x) => x.Documentno),
      Status: [
        CatsStatusCodeEnum.Created.toString(),
        CatsStatusCodeEnum.ReadyForApproval.toString(),
        CatsStatusCodeEnum.Approved.toString(),
      ],
    };
    const humanParams: ISapRequestFilterQueryProps = {
      Employee: reports,
      Fromdate: fromDate,
      Todate: dateHandler.getFilterString(dateHandler.getWeDate()),
      Status: [
        CatsStatusCodeEnum.Created.toString(),
        CatsStatusCodeEnum.ReadyForApproval.toString(),
        CatsStatusCodeEnum.Approved.toString(),
      ],
    };
    let documentObservable$ = this.datahub.apiService.getCats$(documentParams, { select: selectString });
    if (!arrayIsTruthy(userExtDocuments)) documentObservable$ = of([]);
    const humanReportsObservable$ = this.datahub.apiService.getCats$(humanParams, { select: selectString });
    return forkJoin([documentObservable$, humanReportsObservable$]).pipe(
      map(([documentRecords, humanRecords]) => {
        const records: ICatRecord[] = [];
        documentRecords.concat(humanRecords).forEach((x) => {
          if (!records.find((y) => y.Counter === x.Counter)) records.push(x);
        });
        return this.makeResultsObject(records);
      }),
    );
  }

  getBillingRecords$(
    serviceOrder: string | string[],
    wedate: string,
    timesheets: ExtDocument[] = [],
  ): Observable<IMixedCatsType> {
    const params: ISapRequestFilterQueryProps = {
      Fromdate: getFilterString(getWeekStartingDate(moment(wedate))),
      Todate: getFilterString(moment(wedate)),
      RecOrder: serviceOrder,
      Status: [
        CatsStatusCodeEnum.Created.toString(),
        CatsStatusCodeEnum.ReadyForApproval.toString(),
        CatsStatusCodeEnum.Approved.toString(),
      ],
    };
    return this.datahub.apiService.getCats$(params).pipe(
      map((catsRecords: ICatRecord[]) => {
        if (timesheets.length > 0) {
          return this.makeResultsObject(
            catsRecords.filter((x) => !!timesheets.find((y) => safeCompare(x.Extdocumentno, y.Documentno))),
          );
        } else {
          return this.makeResultsObject(catsRecords);
        }
      }),
    );
  }

  // eslint-disable-next-line rxjs-x/finnish -- Not fixing this name for the ECC app
  getIncompletePayrollRecords(costCenter: string | string[], dateHandler: DateHandler) {
    const today = dateHandler.getSapWeDateString(moment());
    let formattedToday = moment(today).format('YYYY-MM-DDT00:00:00');
    formattedToday = `datetime'${formattedToday}'`;

    const params: ISapRequestFilterQueryProps = {
      Fromdate: `datetime'2021-10-14T00:00:00'`,
      Todate: formattedToday,
      CostCenter: costCenter,
      Status: [CatsStatusCodeEnum.Created.toString(), CatsStatusCodeEnum.ReadyForApproval.toString()],
    };
    return this.datahub.apiService.getCats$(params).pipe(map((catsRecords) => this.makeResultsObject(catsRecords)));
  }

  makeResultsObject(catsArray: ICatRecord[]): IMixedCatsType {
    const results: IMixedCatsType = {
      all: [],
      uniqueEmployees: [],
      uniqueEquipment: [],
      employees: [],
      equipment: [],
      productionQuantity: [],
      productionText: [],
    };
    if (!catsArray?.length) return results;
    results.all = catsArray;
    results.employees = this.findEmployeeRecords(catsArray);
    results.equipment = this.findEquipmentRecords(catsArray);
    results.productionQuantity = this.findProductionRecords(catsArray);
    results.productionText = this.findTextProduction(catsArray);
    results.uniqueEmployees = findUnique(catsArray, 'Employeenumber');
    results.uniqueEquipment = findUnique(catsArray, 'Zequipment');

    return results;
  }

  findEmployeeRecords(catsArray: ICatRecord[]): ICatRecord[] {
    //A record is an employee record if it has an abs/att type.
    return catsArray.filter(
      (x) =>
        !!x.AbsAttType ||
        x.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem ||
        x.Zrecordtype === CatsRecordTypeEnum.EmployeeNonBillable ||
        x.Zrecordtype === CatsRecordTypeEnum.EmployeeBillable,
    );
  }

  findEquipmentRecords(catsArray: ICatRecord[]): ICatRecord[] {
    return catsArray.filter((x) => !!x.Zequipment);
  }

  findProductionRecords(catsArray: ICatRecord[]): ICatRecord[] {
    //production records don't store their cost centers in SAP.. annoyingly. So i'm doing it on load.
    return catsArray
      .filter((x) => !!x.Statkeyfig && !x.Longtext && x.Zrecordtype !== CatsRecordTypeEnum.EmployeePerDiem)
      .map((record) => {
        this.datahub.contracts
          .getNewServiceOrder$(record.RecOrder, this.datahub.weDate.getFilterString())
          .pipe(take(1))
          .subscribe({
            next: (x) => {
              record.__costCenter = x?.getCostCenter();
            },
            error: (err) => {
              console.error('Error in findProductionRecords:', err);
            },
          });
        return record;
      });
  }

  findTextProduction(catsArray: ICatRecord[]): ICatRecord[] {
    //production records don't store their cost centers in SAP.. annoyingly. So i'm doing it on load.
    return catsArray
      .filter((x) => !!x.Longtext)
      .map((record) => {
        this.datahub.contracts
          .getNewServiceOrder$(record.RecOrder, this.datahub.weDate.getFilterString())
          .pipe(take(1))
          .subscribe({
            next: (x) => {
              record.__costCenter = x?.getCostCenter();
            },
            error: (err) => {
              console.error('Error in findTextProduction:', err);
            },
          });
        return record;
      });
  }

  filterRecordsByOperation(catRecords: CatsRecord[], providedCategories?: IOutgoingCatsArrays): IOutgoingCatsArrays {
    let create: ICatRecord[] = [];
    let update: ICatRecord[] | Partial<ICatRecord>[] = [];
    let deleted: ICatRecord[] = [];
    if (providedCategories) {
      create = providedCategories.create;
      update = providedCategories.update;
      deleted = providedCategories.delete;
    } else {
      create = [];
      update = [];
      deleted = [];
    }
    catRecords.forEach((record) => {
      const catsFormattedRecord = record.getCatsFormattedRecord();
      if (record.shouldDelete()) {
        deleted.push(catsFormattedRecord);
      } else if (record.shouldCreate()) {
        create.push(catsFormattedRecord);
      } else if (record.shouldUpdate()) {
        update.push(catsFormattedRecord);
      }
    });
    return {
      create: create,
      update: update,
      delete: deleted,
      all: [...create, ...update, ...deleted],
    };
  }

  /**@description 🐈 */
  simpleSingleCats$<T = ICatRecord>(action: CatsActionModeEnum, record: T): Observable<ICatRecord> {
    const outgoingCatsObject = this.makeCatsObject<T>([record], action);

    return this.datahub.apiService.postRecords$(outgoingCatsObject).pipe(map((x) => x[0]));
  }

  /**@description 🐈🐈🐈 */
  simpleMultiCats$<T = ICatRecord>(records: T[], action: CatsActionModeEnum): Observable<ICatRecord[]> {
    const outgoingCatsObject = this.makeCatsObject<T>(records, action);
    return this.datahub.apiService.postRecords$(outgoingCatsObject);
  }

  syncSingleEntityWithCats$(outgoingCatsArrays: IOutgoingCatsArrays): Observable<ICatRecord[]> {
    //runs all three operations if there are values, but only returns the combined values from create and update.
    outgoingCatsArrays.update = outgoingCatsArrays.update.map((x) => this.formatRecordForUpdate(x, this.datahub.user.getEmpId()));
    outgoingCatsArrays.delete.forEach((x) => (x.ModifiedEmpno = this.datahub.user.getEmpId()));
    const update$ = this.datahub.apiService.postRecords$(
      this.makeCatsObject(outgoingCatsArrays.update, CatsActionModeEnum.Update),
    );
    const create$ = this.datahub.apiService.postRecords$(
      this.makeCatsObject(outgoingCatsArrays.create, CatsActionModeEnum.Insert),
    );
    const drop$ = this.datahub.apiService.postRecords$(this.makeCatsObject(outgoingCatsArrays.delete, CatsActionModeEnum.Delete));

    return drop$.pipe(
      switchMap(() =>
        //don't return anything from deleted records, it messes it up bad. Switchmap will run to the new observable
        create$.pipe(
          //once we are done with creating records, Concat map will combine the observables together
          concatMap((creates) =>
            //start web request for updated ones, map will perform synchronous operations once the updated ones are complete
            //combine updates and creates together and return. regular concat
            update$.pipe(map((updates) => creates.concat(updates))),
          ),
        ),
      ),
    );
    //Boo on you haters, I can make a fancy RXJS thing work.
  }

  private formatRecordForUpdate(record: Partial<ICatRecord>, empno: string): Partial<ICatRecord> {
    record.ModifiedEmpno = empno;
    delete record.__costCenter;
    delete record.TargetStatus;
    delete record.Workdate; //Needs to be removed if we want workday date change to be added.
    // SAP sends 12AM 00:00:00 UTC, client converts to local time, when sent back to SAP, if time comes out to 12AM of later, it will move date ahead a day.
    const keys = Object.keys(record);
    keys.forEach((key) => {
      if (key !== undefined && key !== null && !key.endsWith('_X')) {
        record[`${key}_X`] = true;
      }
    });
    delete record.Counter_X;
    return record;
  }

  approve$(extDocuments: ExtDocument[]): Observable<ICatRecord[]> {
    //Add up to 7 zeros at the start of each string
    const justNumbers: string[] = extDocuments.map((x) => String(x.Documentno).padStart(7, '0'));
    return this.datahub.apiService.postRecords$(this.makeApprovalObject(justNumbers));
  }

  private makeApprovalObject(extDocuments: string[]) {
    const approvalShell = this.makeCatsObject([], CatsActionModeEnum.Approval);
    if (!arrayIsTruthy(extDocuments)) return approvalShell;
    approvalShell.d.CatsHeadtoTso = extDocuments.map((x) => this.makeCatsToTso(x));
    return approvalShell;
  }

  private makeCatsToTso(docNo: string): IOutgoingCatsHeadToTso {
    return { Extdocumentno: docNo };
  }

  private makeCatsObject<T = any>(records: T[], action: CatsActionModeEnum): IOutgoingCatRecords<T> {
    return {
      d: {
        Profile: 'ZCATS',
        ReleaseData: true,
        Mode: action,
        CreatedBy: this.datahub.user.getEmpId(),
        CatsHeadtoItem: records,
      },
    };
  }
}

export interface IOutgoingCatsArrays {
  create: ICatRecord[];
  update: Partial<ICatRecord>[];
  delete: ICatRecord[];
  all: Partial<ICatRecord>[];
}
export interface IMixedCatsType {
  all: ICatRecord[];
  uniqueEmployees: string[];
  uniqueEquipment: string[];
  employees: ICatRecord[];
  equipment: ICatRecord[];
  productionQuantity: ICatRecord[];
  productionText: ICatRecord[];
}
